import { useState, useEffect, useCallback, useRef } from 'react'
import type { TermScope, CustomTermConfig, CustomTermsResponse } from '../types/settings'
import {
  getCustomTerms,
  saveCustomTerms,
  deleteCustomTerms,
  getMergedCustomTerms,
  getTermSuggestions,
} from '../services/api'

const CACHE_KEY = 'monkee_custom_terms_merged'
const CACHE_DURATION = 5 * 60 * 1000 // 5 min

type CacheEntry = {
  terms: CustomTermConfig[]
  key: string
  timestamp: number
}

function getCachedMergedTerms(cacheKey: string): CustomTermConfig[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const entry: CacheEntry = JSON.parse(raw)
    if (entry.key !== cacheKey) return null
    if (Date.now() - entry.timestamp > CACHE_DURATION) return null
    return entry.terms
  } catch {
    return null
  }
}

function setCachedMergedTerms(cacheKey: string, terms: CustomTermConfig[]) {
  try {
    const entry: CacheEntry = { terms, key: cacheKey, timestamp: Date.now() }
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry))
  } catch { /* storage full — ignore */ }
}

function invalidateCache() {
  try { localStorage.removeItem(CACHE_KEY) } catch { /* ignore */ }
}

/**
 * Hook para gerenciar termos customizados mesclados para um contexto (pasta + arquivo).
 * Retorna os termos já mesclados (GLOBAL → FOLDER → FILE), prontos para uso.
 */
export function useMergedCustomTerms(folderId?: string | null, fileId?: string | null) {
  const [terms, setTerms] = useState<CustomTermConfig[]>([])
  const [loading, setLoading] = useState(false)
  const lastKey = useRef('')

  const cacheKey = `${folderId || ''}_${fileId || ''}`

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const result = await getMergedCustomTerms(folderId, fileId)
      setTerms(result)
      setCachedMergedTerms(cacheKey, result)
    } catch (err) {
      console.error('Erro ao buscar termos mesclados:', err)
    } finally {
      setLoading(false)
    }
  }, [folderId, fileId, cacheKey])

  useEffect(() => {
    if (cacheKey === lastKey.current) return
    lastKey.current = cacheKey

    const cached = getCachedMergedTerms(cacheKey)
    if (cached) {
      setTerms(cached)
      return
    }
    refresh()
  }, [cacheKey, refresh])

  return { terms, loading, refresh }
}

/**
 * Hook para gerenciar termos de um escopo específico (CRUD).
 */
export function useCustomTermsScope(scope: TermScope, scopeId?: string | null) {
  const [data, setData] = useState<CustomTermsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await getCustomTerms(scope, scopeId || undefined)
      setData(result)
    } catch (err) {
      console.error('Erro ao buscar termos:', err)
    } finally {
      setLoading(false)
    }
  }, [scope, scopeId])

  useEffect(() => { load() }, [load])

  const save = useCallback(async (terms: CustomTermConfig[]) => {
    setSaving(true)
    try {
      const result = await saveCustomTerms(scope, scopeId || null, terms)
      setData(result)
      invalidateCache()
      return result
    } finally {
      setSaving(false)
    }
  }, [scope, scopeId])

  const remove = useCallback(async () => {
    await deleteCustomTerms(scope, scopeId || undefined)
    setData(null)
    invalidateCache()
  }, [scope, scopeId])

  return { data, loading, saving, save, remove, reload: load }
}

/**
 * Hook para sugestões de termos baseadas na análise de um arquivo.
 */
export function useTermSuggestions(fileId?: string | null) {
  const [suggestions, setSuggestions] = useState<CustomTermConfig[]>([])
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    if (!fileId) return
    setLoading(true)
    try {
      const result = await getTermSuggestions(fileId)
      setSuggestions(result)
    } catch (err) {
      console.error('Erro ao buscar sugestões:', err)
    } finally {
      setLoading(false)
    }
  }, [fileId])

  return { suggestions, loading, loadSuggestions: load }
}
